import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";

import { PasswordField } from "@/features/identity/components/PasswordField";
import { useLogin } from "@/features/identity/hooks/useLogin";
import { loginSchema, type LoginFormValues } from "@/features/identity/schemas/auth.schemas";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { showToast } from "@/components/ui/toast";
import { GUEST_ONLY_ROUTES, PUBLIC_ROUTES } from "@/constants/routes";
import { ApiError } from "@/types/api";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginMutation = useLogin();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "", rememberMe: false },
  });

  function onSubmit(values: LoginFormValues) {
    loginMutation.mutate(values, {
      onSuccess: () => {
        const redirectTo = searchParams.get("redirect");
        navigate(redirectTo && redirectTo.startsWith("/") ? redirectTo : PUBLIC_ROUTES.home, {
          replace: true,
        });
      },
      onError: (error) => {
        showToast.error(error instanceof ApiError ? error.message : "Log in failed.");
      },
    });
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-6 py-12">
      <div className="w-full max-w-[400px] space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-h1 text-foreground">Log in</h1>
          <p className="text-body text-muted-foreground">Welcome back to BazaarX.</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" {...register("email")} />
            {errors.email && (
              <p className="text-caption text-destructive" role="alert">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <PasswordField
              id="password"
              autoComplete="current-password"
              {...register("password")}
            />
            {errors.password && (
              <p className="text-caption text-destructive" role="alert">
                {errors.password.message}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Controller
              name="rememberMe"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="rememberMe"
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                  />
                  <Label htmlFor="rememberMe" className="font-normal">
                    Remember me
                  </Label>
                </div>
              )}
            />
            <Link
              to={GUEST_ONLY_ROUTES.forgotPassword}
              className="text-body-sm text-primary underline-offset-4 hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <Button type="submit" fullWidth isLoading={loginMutation.isPending}>
            Log in
          </Button>
        </form>

        <p className="text-center text-body-sm text-muted-foreground">
          Don&rsquo;t have an account?{" "}
          <Link
            to={GUEST_ONLY_ROUTES.register}
            className="text-primary underline-offset-4 hover:underline"
          >
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
